<script>
import EventDetails from './event_entry'
import axios from 'axios'

const QUICK_RANGES = [
  { value: 0, text: 'Any time' },
  { value: 60, text: 'Last hour' },
  { value: 1440, text: 'Last 24 hours' },
  { value: 10080, text: 'Last 7 days' },
  { value: 43200, text: 'Last 30 days' },
  { value: -1, text: 'Custom range' }
]

export default {
  components: {
    EventDetails
  },
  props: {
    basePath: {
      type: String,
      default: '/sharepoint/audit_events'
    }
  },
  data () {
    return {
      // eslint-disable-next-line
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      event: {},
      events: [],
      tableData: [],
      summary: {},
      timeline: [],
      facets: { operations: [], sites: [], users: [], item_types: [], record_types: [] },
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      pageOptions: [10, 25, 50, 100],
      sortBy: 'eventDateTime',
      sortDesc: true,
      isBusy: true,
      isExporting: false,
      loadError: '',
      lastUpdated: null,
      quickRanges: QUICK_RANGES,
      filters: {
        q: '',
        operation: [],
        item_type: [],
        user: '',
        site: '',
        ip: '',
        target: '',
        minutes: 0,
        start: null,
        end: null,
        sharing_only: false,
        unmanaged_only: false
      },
      fields: [
        { key: 'eventDateTime', label: 'Event Time', sortable: true },
        { key: 'operation', label: 'Operation', sortable: true },
        { key: 'userPrincipalName', label: 'User', sortable: true },
        { key: 'target', label: 'Shared With', sortable: false },
        { key: 'objectId', label: 'Resource', sortable: false },
        { key: 'clientIp', label: 'IP', sortable: true },
        { key: 'risk', label: 'Assessment', sortable: false },
        { key: 'action', label: '', sortable: false }
      ]
    }
  },
  computed: {
    baseURL () {
      return this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
    },
    showCustomRange () {
      return this.filters.minutes === -1
    },
    activeFilterCount () {
      const f = this.filters
      let count = 0
      if (f.q) count++
      if (f.operation.length) count++
      if (f.item_type.length) count++
      if (f.user) count++
      if (f.site) count++
      if (f.ip) count++
      if (f.target) count++
      if (f.minutes !== 0) count++
      if (f.sharing_only) count++
      if (f.unmanaged_only) count++
      return count
    },
    timelineSeries () {
      return [{ name: 'Events', data: this.timeline.map((point) => point.count) }]
    },
    timelineOptions () {
      return {
        chart: { type: 'area', sparkline: { enabled: false }, toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        colors: ['#727cf5'],
        fill: { opacity: 0.2 },
        xaxis: {
          categories: this.timeline.map((point) => point.bucket),
          labels: { rotate: -45, style: { fontSize: '10px' } }
        },
        yaxis: { labels: { formatter: (value) => Math.round(value) } },
        tooltip: { x: { show: true } }
      }
    }
  },
  watch: {
    currentPage () {
      this.loadEvents()
    },
    perPage () {
      this.currentPage = 1
      this.loadEvents()
    },
    sortBy () {
      this.currentPage = 1
      this.loadEvents()
    },
    sortDesc () {
      this.currentPage = 1
      this.loadEvents()
    }
  },
  mounted () {
    this.applyFilters()
  },
  methods: {
    // Every request is driven from the same filter object, so the table, the
    // count, the insight cards and the CSV export can never drift apart.
    filterParams () {
      const f = this.filters
      const params = {}
      if (f.q) params.q = f.q
      if (f.operation.length) params.operation = f.operation.join(',')
      if (f.item_type.length) params.item_type = f.item_type.join(',')
      if (f.user) params.user = f.user
      if (f.site) params.site = f.site
      if (f.ip) params.ip = f.ip
      if (f.target) params.target = f.target
      if (f.sharing_only) params.sharing_only = 'yes'
      if (f.unmanaged_only) params.unmanaged_only = 'yes'
      if (f.minutes === -1) {
        if (f.start) params.start = f.start
        if (f.end) params.end = f.end
      } else if (f.minutes > 0) {
        params.minutes = f.minutes
      }
      return params
    },
    queryString (extra = {}) {
      const params = Object.assign({}, this.filterParams(), extra)
      return Object.keys(params)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')
    },
    applyFilters () {
      this.currentPage = 1
      this.loadEvents()
      this.loadInsights()
    },
    resetFilters () {
      this.filters = {
        q: '',
        operation: [],
        item_type: [],
        user: '',
        site: '',
        ip: '',
        target: '',
        minutes: 0,
        start: null,
        end: null,
        sharing_only: false,
        unmanaged_only: false
      }
      this.applyFilters()
    },
    loadEvents () {
      const that = this
      that.isBusy = true
      that.loadError = ''

      const countURL = `${that.baseURL}${that.basePath}/list?${that.queryString({ count: 'yes' })}`
      axios.get(countURL)
        .then((response) => {
          that.totalRows = parseInt(response.data, 10) || 0
        })
        .catch(() => {
          that.totalRows = 0
        })

      const dataURL = `${that.baseURL}${that.basePath}/list?${that.queryString({
        page: that.currentPage,
        page_size: that.perPage,
        altorder: that.sortBy,
        direction: that.sortDesc ? 'desc' : 'asc',
        light: 'yes'
      })}`

      axios.get(dataURL)
        .then((response) => {
          if (response.status === 200) {
            that.events = Array.isArray(response.data) ? response.data : []
            that.tableData = that.events.map((entry) => {
              const audit = entry.auditData || {}
              return {
                eventId: entry.eventId || '',
                eventDateTime: entry.eventDateTime || '',
                operation: entry.operation || '',
                userPrincipalName: entry.userPrincipalName || '',
                target: audit.TargetUserOrGroupName || '',
                targetType: audit.TargetUserOrGroupType || '',
                objectId: entry.objectId || '',
                siteUrl: entry.siteUrl || '',
                itemType: audit.ItemType || '',
                clientIp: entry.clientIp || audit.ClientIP || '',
                isManagedDevice: audit.IsManagedDevice,
                risk: entry.risk || { level: 'low', reasons: [] }
              }
            })
            that.lastUpdated = new Date()
          }
          that.isBusy = false
        })
        .catch((error) => {
          that.loadError = (error && error.message) || 'Unable to load SharePoint audit events.'
          that.tableData = []
          that.isBusy = false
        })
    },
    loadInsights () {
      const that = this
      const query = that.queryString()

      axios.get(`${that.baseURL}${that.basePath}/summary?${query}`)
        .then((response) => {
          that.summary = response.data || {}
        })
        .catch(() => {
          that.summary = {}
        })

      axios.get(`${that.baseURL}${that.basePath}/timeline?${query}&bucket=day`)
        .then((response) => {
          that.timeline = Array.isArray(response.data) ? response.data : []
        })
        .catch(() => {
          that.timeline = []
        })

      // Facets are loaded unfiltered so the dropdowns always offer every value
      // that exists, instead of collapsing to whatever the current filter left.
      axios.get(`${that.baseURL}${that.basePath}/facets`)
        .then((response) => {
          that.facets = response.data || that.facets
        })
        .catch(() => {})
    },
    exportCsv () {
      const that = this
      that.isExporting = true
      const url = `${that.baseURL}${that.basePath}/list/csv?${that.queryString({ limit: 5000 })}`
      axios.get(url, { responseType: 'blob' })
        .then((response) => {
          const blob = new Blob([response.data], { type: 'text/csv' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'sharepoint_audit_events.csv'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(link.href)
        })
        .catch((error) => {
          that.loadError = (error && error.message) || 'CSV export failed.'
        })
        .then(() => {
          that.isExporting = false
        })
    },
    showDetails (eventId) {
      if (eventId === undefined) return
      this.event = this.events.find((entry) => entry.eventId === eventId) || {}
      this.$refs['modal-details'].show()
    },
    // Clicking a value in the insight cards narrows the view to it, so the
    // "top users / top sites" panels double as one-click drilldowns.
    drillDown (field, value) {
      if (field === 'operation') {
        this.filters.operation = [value]
      } else if (field === 'user') {
        this.filters.user = value
      } else if (field === 'site') {
        this.filters.site = value
      }
      this.applyFilters()
    },
    formatDate (value) {
      if (!value) return ''
      const parsed = new Date(value)
      return isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
    },
    camelCaseToSentence (text) {
      if (!text) return ''
      const result = String(text).replace(/([A-Z])/g, ' $1')
      return result.charAt(0).toUpperCase() + result.slice(1)
    },
    // Object ids are full SharePoint URLs; showing the tail keeps the column
    // readable while the title attribute preserves the whole path.
    shortResource (value) {
      if (!value) return ''
      const decoded = decodeURIComponent(value)
      const parts = decoded.split('/').filter((part) => part.length > 0)
      return parts.length > 2 ? '.../' + parts.slice(-2).join('/') : decoded
    },
    riskVariant (level) {
      return { high: 'danger', medium: 'warning', low: 'success' }[level] || 'secondary'
    }
  }
}
</script>

<template>
  <div>
    <b-alert
      :show="loadError.length > 0"
      variant="danger"
      dismissible
      @dismissed="loadError = ''"
    >
      {{ loadError }}
    </b-alert>

    <!-- Insight cards -->
    <div class="row">
      <div class="col-md-3 col-6">
        <div class="card mb-2">
          <div class="card-body py-2">
            <h5 class="text-muted font-size-13 mb-1">
              Events
            </h5>
            <h3 class="mb-0">
              {{ summary.total || 0 }}
            </h3>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="card mb-2">
          <div class="card-body py-2">
            <h5 class="text-muted font-size-13 mb-1">
              Users
            </h5>
            <h3 class="mb-0">
              {{ summary.unique_users || 0 }}
            </h3>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="card mb-2">
          <div class="card-body py-2">
            <h5 class="text-muted font-size-13 mb-1">
              Sites
            </h5>
            <h3 class="mb-0">
              {{ summary.unique_sites || 0 }}
            </h3>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="card mb-2">
          <div class="card-body py-2">
            <h5 class="text-muted font-size-13 mb-1">
              External Shares
            </h5>
            <h3
              class="mb-0"
              :class="{ 'text-danger': (summary.external_shares || 0) > 0 }"
            >
              {{ summary.external_shares || 0 }}
            </h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Activity over time + top values -->
    <div class="row">
      <div class="col-lg-7">
        <div class="card mb-2">
          <div class="card-body py-2">
            <h5 class="font-size-14 mb-1">
              Activity over time
            </h5>
            <apexchart
              v-if="timeline.length > 0"
              type="area"
              height="180"
              :series="timelineSeries"
              :options="timelineOptions"
            />
            <div
              v-else
              class="text-muted py-4 text-center"
            >
              No activity for the selected filters.
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-5">
        <div class="card mb-2">
          <div class="card-body py-2">
            <b-tabs
              small
              content-class="pt-2"
            >
              <b-tab
                title="Operations"
                active
              >
                <p
                  v-if="!summary.top_operations || summary.top_operations.length === 0"
                  class="text-muted mb-0"
                >
                  No data.
                </p>
                <div
                  v-for="row in summary.top_operations"
                  :key="row.value"
                  class="d-flex justify-content-between sp-drill"
                  @click="drillDown('operation', row.value)"
                >
                  <span>{{ camelCaseToSentence(row.value) }}</span>
                  <b-badge variant="light">
                    {{ row.count }}
                  </b-badge>
                </div>
              </b-tab>
              <b-tab title="Users">
                <p
                  v-if="!summary.top_users || summary.top_users.length === 0"
                  class="text-muted mb-0"
                >
                  No data.
                </p>
                <div
                  v-for="row in summary.top_users"
                  :key="row.value"
                  class="d-flex justify-content-between sp-drill"
                  @click="drillDown('user', row.value)"
                >
                  <span class="text-truncate mr-2">{{ row.value }}</span>
                  <b-badge variant="light">
                    {{ row.count }}
                  </b-badge>
                </div>
              </b-tab>
              <b-tab title="Sites">
                <p
                  v-if="!summary.top_sites || summary.top_sites.length === 0"
                  class="text-muted mb-0"
                >
                  No data.
                </p>
                <div
                  v-for="row in summary.top_sites"
                  :key="row.value"
                  class="d-flex justify-content-between sp-drill"
                  @click="drillDown('site', row.value)"
                >
                  <span
                    class="text-truncate mr-2"
                    :title="row.value"
                  >{{ row.value }}</span>
                  <b-badge variant="light">
                    {{ row.count }}
                  </b-badge>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-2">
      <div class="card-body py-2">
        <div class="row">
          <div class="col-md-4 mb-2">
            <label class="font-size-12 mb-1">Search</label>
            <b-form-input
              v-model="filters.q"
              type="search"
              size="sm"
              placeholder="User, site, file, IP, operation..."
              @keyup.enter="applyFilters"
            />
          </div>
          <div class="col-md-2 mb-2">
            <label class="font-size-12 mb-1">Time range</label>
            <b-form-select
              v-model="filters.minutes"
              size="sm"
              :options="quickRanges"
            />
          </div>
          <template v-if="showCustomRange">
            <div class="col-md-2 mb-2">
              <label class="font-size-12 mb-1">From</label>
              <flat-pickr
                v-model="filters.start"
                :config="{ enableTime: true, dateFormat: 'Y-m-d H:i' }"
                class="form-control form-control-sm"
                placeholder="Start"
              />
            </div>
            <div class="col-md-2 mb-2">
              <label class="font-size-12 mb-1">To</label>
              <flat-pickr
                v-model="filters.end"
                :config="{ enableTime: true, dateFormat: 'Y-m-d H:i' }"
                class="form-control form-control-sm"
                placeholder="End"
              />
            </div>
          </template>
          <div class="col-md-3 mb-2">
            <label class="font-size-12 mb-1">Operation</label>
            <b-form-select
              v-model="filters.operation"
              size="sm"
              multiple
              :select-size="1"
            >
              <b-form-select-option
                v-for="row in facets.operations"
                :key="row.value"
                :value="row.value"
              >
                {{ camelCaseToSentence(row.value) }} ({{ row.count }})
              </b-form-select-option>
            </b-form-select>
          </div>
          <div class="col-md-3 mb-2">
            <label class="font-size-12 mb-1">Item type</label>
            <b-form-select
              v-model="filters.item_type"
              size="sm"
              multiple
              :select-size="1"
            >
              <b-form-select-option
                v-for="row in facets.item_types"
                :key="row.value"
                :value="row.value"
              >
                {{ row.value }} ({{ row.count }})
              </b-form-select-option>
            </b-form-select>
          </div>
        </div>

        <div class="row">
          <div class="col-md-3 mb-2">
            <label class="font-size-12 mb-1">User contains</label>
            <b-form-input
              v-model="filters.user"
              size="sm"
              placeholder="user@domain"
              @keyup.enter="applyFilters"
            />
          </div>
          <div class="col-md-3 mb-2">
            <label class="font-size-12 mb-1">Site contains</label>
            <b-form-input
              v-model="filters.site"
              size="sm"
              placeholder="/teams/..."
              @keyup.enter="applyFilters"
            />
          </div>
          <div class="col-md-2 mb-2">
            <label class="font-size-12 mb-1">Client IP</label>
            <b-form-input
              v-model="filters.ip"
              size="sm"
              placeholder="10.0.0.1"
              @keyup.enter="applyFilters"
            />
          </div>
          <div class="col-md-2 mb-2">
            <label class="font-size-12 mb-1">Shared with</label>
            <b-form-input
              v-model="filters.target"
              size="sm"
              placeholder="target account"
              @keyup.enter="applyFilters"
            />
          </div>
          <div class="col-md-2 mb-2 d-flex align-items-end">
            <div>
              <b-form-checkbox
                v-model="filters.sharing_only"
                switch
                class="font-size-12"
              >
                Sharing only
              </b-form-checkbox>
              <b-form-checkbox
                v-model="filters.unmanaged_only"
                switch
                class="font-size-12"
              >
                Unmanaged device
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <b-button
              size="sm"
              variant="primary"
              @click="applyFilters"
            >
              Apply filters
            </b-button>
            <b-button
              size="sm"
              variant="light"
              class="ml-1"
              @click="resetFilters"
            >
              Reset
            </b-button>
            <b-button
              size="sm"
              variant="light"
              class="ml-1"
              :disabled="isExporting"
              @click="exportCsv"
            >
              <b-spinner
                v-if="isExporting"
                small
              />
              Export CSV
            </b-button>
            <span
              v-if="activeFilterCount > 0"
              class="text-muted ml-2 font-size-12"
            >
              {{ activeFilterCount }} filter(s) active
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div class="row mb-md-2">
      <div class="col-sm-12">
        <span>
          Events found: <b>{{ totalRows }}</b>
          <template v-if="lastUpdated">
            at <b>{{ lastUpdated.toLocaleString() }}</b>
          </template>
        </span>
      </div>
    </div>

    <div class="table-responsive mb-0">
      <b-table
        :items="tableData"
        :fields="fields"
        :busy="isBusy"
        responsive="sm"
        :per-page="0"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :no-local-sorting="true"
        show-empty
        empty-text="No SharePoint audit events match the current filters."
      >
        <template v-slot:table-busy>
          <div class="text-center">
            <br><br>
            <b-spinner class="align-middle" />
            <strong>&nbsp;&nbsp;Loading...</strong>
          </div>
        </template>

        <template v-slot:cell(eventDateTime)="data">
          {{ formatDate(data.item.eventDateTime) }}
        </template>

        <template v-slot:cell(operation)="data">
          {{ camelCaseToSentence(data.item.operation) }}
          <div
            v-if="data.item.itemType"
            class="text-muted font-size-11"
          >
            {{ data.item.itemType }}
          </div>
        </template>

        <template v-slot:cell(target)="data">
          <template v-if="data.item.target">
            {{ data.item.target }}
            <div
              v-if="data.item.targetType"
              class="text-muted font-size-11"
            >
              {{ data.item.targetType }}
            </div>
          </template>
          <span
            v-else
            class="text-muted"
          >—</span>
        </template>

        <template v-slot:cell(objectId)="data">
          <a
            v-if="data.item.objectId"
            :href="data.item.objectId"
            :title="data.item.objectId"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ shortResource(data.item.objectId) }}
          </a>
          <span
            v-else
            class="text-muted"
          >—</span>
        </template>

        <template v-slot:cell(clientIp)="data">
          {{ data.item.clientIp || '—' }}
          <feather
            v-if="data.item.isManagedDevice === false"
            type="smartphone"
            class="icon-xs text-warning"
            title="Unmanaged device"
          />
        </template>

        <template v-slot:cell(risk)="data">
          <b-badge
            :variant="riskVariant(data.item.risk.level)"
            :title="data.item.risk.reasons.join(' · ')"
          >
            {{ data.item.risk.level }}
          </b-badge>
        </template>

        <template v-slot:cell(action)="data">
          <b-dropdown
            right
            variant="white"
            class="align-self-center"
            toggle-class="p-0"
          >
            <template v-slot:button-content>
              <feather
                type="chevron-down"
                class="align-middle"
                style="height:12px !important;width:20px !important;"
              />
            </template>
            <b-dropdown-item
              class="notify-item"
              @click="showDetails(data.item.eventId)"
            >
              <feather
                type="search"
                class="icon-dual icon-xs mr-2 align-middle"
              />
              <span>Details</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </div>

    <div class="row">
      <div class="col-sm-12 col-md-6">
        <div class="dataTables_length">
          <label class="d-inline-flex align-items-center">
            Show&nbsp;
            <b-form-select
              v-model="perPage"
              size="sm"
              :options="pageOptions"
            />&nbsp;entries
          </label>
        </div>
      </div>
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-right">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
            />
          </ul>
        </div>
      </div>
    </div>

    <b-modal
      id="sharepoint-event-details"
      ref="modal-details"
      size="xl"
      title="SharePoint Audit Event"
      :centered="true"
      :no-fade="true"
      ok-only
    >
      <EventDetails :event="event" />
    </b-modal>
  </div>
</template>

<style scoped>
  .sp-drill {
    cursor: pointer;
    padding: 2px 0;
  }

  .sp-drill:hover {
    background-color: #f4f6fb;
  }

  .font-size-11 {
    font-size: 11px;
  }
</style>
